import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getUserData,
  // getDefaultDateRange
} from '../util';
import { SearchStorageManager } from '../util/searchStorageManager';
import { logErrorToSplunk } from '../../../service';
import type {
  SearchState,
  SearchResult,
  SetFilterPayload,
  // SearchFilters,
  SearchCriteriaElements,
  SearchCriteria,
  PaginationData
} from './types';

const { NO_OF_RESORT } = window.weeksWebCloudConfig;
// const defaultDateRange = getDefaultDateRange();

// Helper function to load searchCriteriaElements from new localStorage structure
// const loadFromLocalStorage = (): SearchCriteriaElements | null => {
//   try {
//     const searchCriteriaElements = SearchStorageManager.getItem('localStoragesearchCriteriaElements');
//     if (searchCriteriaElements) {
//       console.log('🚀 [searchSlice] Loaded searchCriteriaElements from new localStorage structure:', searchCriteriaElements);
//       return searchCriteriaElements;
//     }
//   } catch (error) {
//     console.error('❌ [searchSlice] Error loading from new localStorage structure:', error);
//   }
//   return null;
// };

// Load from localStorage or use defaults
// const storedSearchCriteria = loadFromLocalStorage();

// Read ?onSale=true from the URL at slice initialisation time
const urlOnSale = new URLSearchParams(window.location.search).get('onSale');
const initialOnSaleInventory = urlOnSale === 'true';

// Updated initial state - initialize from localStorage if available
const initialState: SearchState = {
  searchTerm: '',
  results: [],
  isLoading: false,
  error: null,
  filters: {
    location: '',
    dateRange: '',
    priceRange: '',
  },
  searchCriteriaElements: {
    language: getUserData().language.code,
    locale: getUserData().locale,
    numberOfResortsToReturn: 0,
    productType: '',
    productTypes: [],
    recordRollup: false,
    filters: [],
    membershipType: '',
    searchCriteria: {
      distanceUnit: '',
      minStartDate: '',
      maxStartDate: '',
      onSaleInventory: initialOnSaleInventory,
      preferredCheckInDate: '',
      resortSearchCriteria: [],
      youChoseFilters: [],
      minLoS: 1,
      maxLoS: 7
    },
    pointsExchangeSearchType: 'ALL',
    selectedDeposit: '',
    showNonBookable: false,
    pagination: {
      from: 0,
      size: Number(NO_OF_RESORT)
    },
    customerId: getUserData().memberId || '',
    visibleNightlyRentalInv: false,
    searchNightlyRentalInvSelected: false,
    selectedDepositTP: '',
  },
  //[for testing only] change/adjust logic once offerId logic is implemented
  isOfferApplied: true,
  isDateSelectedFromProgressiveBar: null,
};

// Existing async thunk
export const performSearch = createAsyncThunk<SearchResult[], string, { rejectValue: string }>(
  'search/performSearch',
  async (searchParams, { rejectWithValue }) => {
    const serviceUrl = window.weeksWebCloudConfig?.REACT_APP_SERVICE_URL || '';
    const url = `${serviceUrl}/searchTypeahead/inventory/en?Ntt=flo*&consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER&customerIdQ=045062689`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchParams),
      });
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const data = await response.json() as SearchResult[];
      return data;
    } catch (error) {
      try {
        logErrorToSplunk(error, url, 'POST', 'Search - performSearch');
      } catch (logError) {
        console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

// Create the slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    setFilter(state, action: PayloadAction<SetFilterPayload>) {
      const { filterType, value } = action.payload;
      state.filters[filterType] = value;
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },
    clearSearchResults(state) {
      state.results = [];
    },
    // New reducers for searchCriteriaElements
    updateSearchCriteriaElements: (state, action: PayloadAction<Partial<SearchCriteriaElements>>) => {
      state.searchCriteriaElements = { ...state.searchCriteriaElements, ...action.payload };
      // UPDATED: Use new storage manager
      try {
        SearchStorageManager.setItem('loadSearchCriteriaElements', state.searchCriteriaElements);
      } catch (error) {
        console.error('❌ Error saving searchCriteriaElements to new localStorage structure:', error);
      }
    },
    updateSearchCriteria: (
      state, 
      action: PayloadAction<{ searchCriteria: Partial<SearchCriteria>; productType?: string }>
    ) => {
      const pt = action.payload.productType;
      
      // FIXED: Use Object.assign to update searchCriteria properties
      state.searchCriteriaElements.searchCriteria = {
        ...state.searchCriteriaElements.searchCriteria,
        ...action.payload.searchCriteria
      };
      
      // Handle productType if provided
      if (pt) {
        if (pt.includes('-')) {
          const productTypesArray = pt.split('-');
          if(productTypesArray.length > 2){
            state.searchCriteriaElements.productType = 'All'; // Set to 'All' for multiple types
            state.searchCriteriaElements.productTypes = []; // Clear array for single type
          }else{
          state.searchCriteriaElements.productTypes = productTypesArray;
          state.searchCriteriaElements.productType = ''; // Set to empty string for array case
          }
        } else {
          if (pt.toLowerCase() === 'extravacation') {
            state.searchCriteriaElements.productType = '';
            state.searchCriteriaElements.productTypes = ['ExtraVacation', 'LastCall'];
          } else {
            state.searchCriteriaElements.productType = pt;
            state.searchCriteriaElements.productTypes = [];
          }
           // Clear array for single type
        }
      }
      // UPDATED: Use new storage manager
      try {
        SearchStorageManager.setItem('localStoragesearchCriteriaElements', state.searchCriteriaElements);
        // console.log('✅ Successfully saved searchCriteria to new localStorage structure');
      } catch (error) {
        console.error('❌ Error saving searchCriteria to new localStorage structure:', error);
      }
    },
    // updateTypeahead: (
    //   state: SearchState,
    //   action: PayloadAction<{ typeahead: TypeaheadData; productType: string }>
    // ) => {
    //   const pt = action.payload.productType;
      
    //   // Delete location-related properties
    //   delete state.searchCriteriaElements.searchCriteria.radius;
    //   delete state.searchCriteriaElements.searchCriteria.latitude;
    //   delete state.searchCriteriaElements.searchCriteria.longitude;
      
    //   // Update searchCriteria with new typeahead
    //   state.searchCriteriaElements.searchCriteria = {
    //     ...state.searchCriteriaElements.searchCriteria,
    //     typeahead: action.payload.typeahead
    //   };
      
    //   // Handle productType if provided - same logic as updateSearchCriteria
    //   if (pt) {
    //     if (pt.includes('-')) {
    //       const productTypesArray = pt.split('-');
    //       state.searchCriteriaElements.productTypes = productTypesArray;
    //       state.searchCriteriaElements.productType = ''; // Set to empty string for array case
    //     } else {
    //       state.searchCriteriaElements.productType = pt;
    //       state.searchCriteriaElements.productTypes = []; // Clear array for single type
    //     }
    //   }
      
    //   console.log('updateTypeahead:', JSON.stringify(state.searchCriteriaElements));

    //   // UPDATED: Use new storage manager
    //   // try {
    //   //   SearchStorageManager.setItem('localStoragesearchCriteriaElements', state.searchCriteriaElements);
    //   //   console.log('✅ Successfully saved typeahead to new localStorage structure');
    //   // } catch (error) {
    //   //   console.error('❌ Error saving typeahead to new localStorage structure:', error);
    //   // }
    // },
    updatePagination: (state, action: PayloadAction<Partial<PaginationData>>) => {
      state.searchCriteriaElements.pagination = { 
        ...state.searchCriteriaElements.pagination, 
        ...action.payload 
      };
    },
    resetSearchCriteriaElements: (state) => {
      state.searchCriteriaElements = initialState.searchCriteriaElements;
    },
    setDateSelectedFromProgressiveBar: (state, action: PayloadAction<Date | null>) => {
      state.isDateSelectedFromProgressiveBar = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.results = action.payload;
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

// Export the actions and reducer
export const { 
  setSearchTerm, 
  setFilter, 
  clearFilters, 
  clearSearchResults,
  updateSearchCriteriaElements,
  updateSearchCriteria,
  updatePagination,
  resetSearchCriteriaElements,
  setDateSelectedFromProgressiveBar
} = searchSlice.actions;

export default searchSlice.reducer;